import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Activity, PhoneCall, Radio, Users2, X, ArrowDown, ArrowUp, AlertTriangle, UserX } from "lucide-react";
import { wsUrl } from "@/lib/api";
import { ChannelMeter, EmptyState, PageHeader, StatusPill } from "@/components/uiext";
import { StatusSquare } from "./_tenantCommon.jsx";
import { SectionHelp } from "@/lib/help.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useEngineAliveDebounced } from "@/lib/useEngineAlive";

const REG_STATUS_KIND = { up: "ok", stale: "warn", unreachable: "danger" };

/** Phase 6n-f — colour the trace timeline rows the way operators read them:
 *  - 1xx provisional → info; 2xx success → ok; 3xx-4xx-5xx → warn/danger;
 *  - "auth retry" / "spoof_reject" / "BYE" summaries get accent treatment.
 *  Returns the `kind` consumed by `StatusSquare`. */
function traceStatusKind(tr) {
  const m = (tr.method || "").toUpperCase();
  const s = Number(tr.status || 0);
  if (s >= 500) return "danger";
  if (s >= 400) return "warn";
  if (s >= 300) return "warn";
  if (s >= 200 && s < 300) return "ok";
  if (s >= 100 && s < 200) return "info";
  if (m === "INVITE" || m === "REGISTER") return "info";
  if (m === "BYE" || m === "CANCEL") return "offline";
  if ((tr.summary || "").toLowerCase().includes("reject") ||
      (tr.summary || "").toLowerCase().includes("fail")) return "danger";
  return "info";
}

function fmtUptime(seconds) {
  if (!seconds || seconds < 0) return "0s";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function LivePage() {
  const { t } = useTranslation();
  const [snap, setSnap] = useState({ active_calls: [], counters: [], recent_security: [] });
  const [tenants, setTenants] = useState({});
  const [trunks, setTrunks] = useState({});
  const [perf, setPerf] = useState(null);
  const [regs, setRegs] = useState([]);
  // Phase 6am (Hotfix Z.6) — Failed REGISTER attempts panel.
  // Polls the tenant ingress-traces feed every 5s filtered to
  // method=REGISTER. Every REGISTER the engine rejected (before
  // attributing it to a customer) lands here with attempted_domain,
  // attempted_user, peer_ip, status, and a plain-English `reason`.
  // This is the single click softphone-troubleshooter operators kept
  // asking for ("Zoiper says Failed — what does the engine see?").
  const [registerFails, setRegisterFails] = useState([]);
  // Phase 6n-f — Live Traffic master-detail trace pane.
  // selectedCallId controls which active call's SIP trace timeline
  // is shown on the right; null = no detail pane open.
  const [selectedCallId, setSelectedCallId] = useState(null);
  const [traceData, setTraceData] = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const wsRef = useRef(null);
  // Phase 6ak (Hotfix Z.4) — debounce engine_alive flicker from the
  // Live WS (1Hz push). 3 consecutive misses ≈ 3s before flipping to
  // "ENGINE DOWN" — until then the badge shows "CHECKING…".
  // Pass `snap` as `tick` so a stuck-false primitive still advances
  // the consecutive-miss counter.
  const engineState = useEngineAliveDebounced(snap.engine_alive, 3, snap);

  useEffect(() => {
    (async () => {
      const [tr, cu] = await Promise.all([api.get("/tenants"), api.get("/trunks")]);
      const tMap = {};
      tr.data.forEach((t) => (tMap[t.id] = t));
      setTenants(tMap);
      const trMap = {};
      cu.data.forEach((t) => (trMap[t.id] = t));
      setTrunks(trMap);
    })();

    let cancelled = false;
    const connect = () => {
      const ws = new WebSocket(wsUrl("/ws/live"));
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          setSnap(JSON.parse(ev.data));
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        if (!cancelled) setTimeout(connect, 1500);
      };
    };
    connect();

    // Engine perf + registrations + register-fail-feed poll every 5s
    // (cheap, cache-friendly).
    const pullPerf = async () => {
      try {
        const [p, r, rf] = await Promise.all([
          api.get("/live/engine-perf"),
          api.get("/live/registrations"),
          api.get("/ingress-traces?method=REGISTER&limit=20"),
        ]);
        if (cancelled) return;
        setPerf(p.data);
        setRegs(r.data || []);
        setRegisterFails((rf.data && rf.data.traces) || []);
      } catch { /* ignore */ }
    };
    pullPerf();
    const itv = setInterval(pullPerf, 5000);

    return () => {
      cancelled = true;
      clearInterval(itv);
      try {
        wsRef.current?.close();
      } catch { /* ignore */ }
    };
  }, []);

  // Phase 6n-f — Trace polling for the selected active call.
  // Polls every 1.5 s while a call is selected so the timeline grows
  // in real time as new SIP messages arrive. Stops cleanly when the
  // operator deselects or closes the pane. We also auto-deselect if
  // the call drops off the active list (engine cleared it).
  useEffect(() => {
    if (!selectedCallId) {
      setTraceData(null);
      setTraceLoading(false);
      return;
    }
    let cancelled = false;
    let itv = null;
    const pull = async () => {
      try {
        const r = await api.get(`/live/calls/${selectedCallId}/traces`);
        if (cancelled) return;
        setTraceData(r.data);
        setTraceLoading(false);
      } catch {
        if (cancelled) return;
        setTraceLoading(false);
      }
    };
    setTraceLoading(true);
    pull();
    itv = setInterval(pull, 1500);
    return () => {
      cancelled = true;
      if (itv) clearInterval(itv);
    };
  }, [selectedCallId]);

  // Auto-deselect when the call disappears from the active list.
  useEffect(() => {
    if (!selectedCallId) return;
    const stillActive = snap.active_calls.some((c) => c.call_id === selectedCallId);
    if (!stillActive && snap.active_calls.length > 0) {
      // call ended — keep the trace pane open one extra render so the
      // operator can see the final BYE / 200, then auto-close after 6 s
      const timer = setTimeout(() => setSelectedCallId(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [selectedCallId, snap.active_calls]);

  const callsByTenant = snap.counters.filter((c) => c.scope === "customer");

  // Phase 6h — friendly-name resolver.
  // Server-side now embeds `tenant_name` / `trunk_name` / `org_name` /
  // `reseller_name` on every live_call doc and `name` on every counter
  // doc. We still keep the client-side lookup tables as a fallback so
  // older in-flight rows (engines pre-6h) still render names during a
  // rolling upgrade.
  const tenantName = (id, embedded = "") =>
    embedded || tenants[id]?.name || (id ? `${String(id).slice(0, 8)}…` : "—");
  const trunkName = (id, embedded = "") =>
    embedded || trunks[id]?.name || (id ? `${String(id).slice(0, 8)}…` : "—");

  return (
    <div data-testid="page-live">
      <PageHeader
        title={t("pages.live.title")}
        subtitle={t("pages.live.subtitle")}
        icon={Radio}
        actions={
          <StatusPill
            kind={
              engineState === "up"
                ? "ok"
                : engineState === "down"
                ? "danger"
                : "warn"
            }
          >
            {engineState === "up" ? (
              <>
                <Radio className="h-3 w-3 mr-1 pulse-live" /> LIVE
              </>
            ) : engineState === "down" ? (
              t("pages.live.engineDown", "ENGINE DOWN")
            ) : (
              t("dashboard.engine.checking", "Checking…")
            )}
          </StatusPill>
        }
      />
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_0.6fr] gap-4">
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="text-sm font-medium flex items-center gap-2">
                <PhoneCall className="h-4 w-4 text-accent" />
                {t("pages.live.activeCalls", "Active calls")}
                <span className="text-muted-foreground font-mono">
                  ({snap.active_calls.length})
                </span>
              </div>
            </div>
            {snap.active_calls.length === 0 ? (
              <div className="p-10">
                <EmptyState
                  title={t("pages.live.emptyTitle", "Silence on the wire")}
                  description={t("pages.live.emptyDesc", "No calls in progress. When calls come in they'll appear here live.")}
                />
              </div>
            ) : (
              <div className="max-h-[560px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky-table-head">
                    <TableRow>
                      <TableHead>{t("pages.live.cols.customer", "Customer")}</TableHead>
                      <TableHead>{t("pages.live.cols.from", "From")}</TableHead>
                      <TableHead>{t("pages.live.cols.to", "To")}</TableHead>
                      <TableHead>{t("pages.live.cols.trunk", "Trunk")}</TableHead>
                      <TableHead>{t("pages.live.cols.direction", "Dir")}</TableHead>
                      <TableHead>{t("pages.live.cols.state", "State")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {snap.active_calls.map((c) => {
                      const isSelected = c.call_id === selectedCallId;
                      return (
                        <TableRow
                          key={c.call_id}
                          data-testid={`live-row-${c.call_id}`}
                          onClick={() => setSelectedCallId(isSelected ? null : c.call_id)}
                          className={
                            "cursor-pointer transition-colors " +
                            (isSelected
                              ? "bg-[hsl(var(--accent)/0.10)] border-l-2 border-[hsl(var(--accent))]"
                              : "hover:bg-muted/40")
                          }
                        >
                          <TableCell className="font-medium">
                            <span title={c.tenant_id || ""}>
                              {tenantName(c.tenant_id, c.tenant_name)}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{c.from}</TableCell>
                          <TableCell className="font-mono text-xs">{c.to}</TableCell>
                          <TableCell className="text-xs">
                            <span title={c.trunk_id || ""}>
                              {trunkName(c.trunk_id, c.trunk_name)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <StatusPill kind="info">{c.direction}</StatusPill>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <StatusSquare
                                kind={
                                  c.state === "answered"
                                    ? "ok"
                                    : c.state === "ringing" || c.state === "trying"
                                      ? "warn"
                                      : c.state === "failed"
                                        ? "danger"
                                        : "info"
                                }
                                title={c.state}
                              />
                              <span className="text-xs capitalize">{c.state}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Phase 6n-f — SIP trace timeline pane (master-detail).
            Shows the chronological event log for the active call the
            operator clicked: REGISTER · INVITE · 100 Trying · 183 ·
            200 OK · ACK · BYE etc. Polls every 1.5 s while open.
            Lives in the same left column as Active Calls so on wide
            viewports you get list-on-top / timeline-below; on lg+
            screens it nests cleanly under the table. */}
        {selectedCallId && (
          <TraceTimelinePane
            t={t}
            callId={selectedCallId}
            data={traceData}
            loading={traceLoading}
            onClose={() => setSelectedCallId(null)}
          />
        )}
        <div className="space-y-4">
          <Card data-testid="live-channels-card">
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-2">
                {t("pages.live.channelsInUse", "Channels in use")}
              </div>
              {(() => {
                // Phase 6am (Hotfix Z.6) — server now embeds caps +
                // channel_policy + name on every counter row. Show
                // every entity that either has live usage OR has at
                // least one configured cap (so operators see "0/10
                // out" BEFORE they hit "10/10 out" and get rejected).
                // Empty (uncapped + idle) rows are dropped — they're
                // pure noise.
                const visible = (snap.counters || []).filter((c) => {
                  const usage = (c.total ?? c.value ?? 0) + (c.in || 0) + (c.out || 0);
                  const capped =
                    (c.cap_total || 0) > 0 ||
                    (c.cap_in || 0) > 0 ||
                    (c.cap_out || 0) > 0;
                  return usage > 0 || capped;
                });
                if (visible.length === 0) {
                  return (
                    <div className="text-xs text-muted-foreground">
                      {t("pages.live.noUsage", "No usage registered yet.")}
                    </div>
                  );
                }
                return (
                  <div className="space-y-3">
                    {visible.map((c) => {
                      // Prefer server-supplied fields; gracefully fall
                      // back to the legacy client-side lookup tables
                      // so older WS payloads keep rendering during a
                      // rolling upgrade.
                      const tn =
                        c.scope === "customer" || c.scope === "reseller"
                          ? tenants[c.scope_id]
                          : null;
                      const policy =
                        c.channel_policy ||
                        tn?.channel_policy ||
                        (c.scope === "trunk"
                          ? trunks[c.scope_id]?.channel_policy || "total"
                          : "total");
                      const cap_total =
                        c.cap_total ??
                        Number(
                          (c.scope === "trunk"
                            ? trunks[c.scope_id]?.channels
                            : tn?.limits?.channels) || 0,
                        );
                      const cap_in =
                        c.cap_in ??
                        Number(
                          (c.scope === "trunk"
                            ? trunks[c.scope_id]?.channels_in
                            : tn?.limits?.channels_in) || 0,
                        );
                      const cap_out =
                        c.cap_out ??
                        Number(
                          (c.scope === "trunk"
                            ? trunks[c.scope_id]?.channels_out
                            : tn?.limits?.channels_out) || 0,
                        );
                      const idShort = `${c.scope_id?.slice(0, 8)}…`;
                      let label;
                      if (c.scope === "customer") {
                        label = c.name || tn?.name || idShort;
                      } else if (c.scope === "trunk") {
                        label = c.name || trunks[c.scope_id]?.name || idShort;
                      } else if (c.scope === "reseller") {
                        label = c.name || tn?.name || idShort;
                      } else if (c.scope === "org") {
                        label = c.name || idShort;
                      } else {
                        label = `${c.scope}: ${idShort}`;
                      }
                      const scopeBadge =
                        {
                          customer: t("pages.live.scope.customer", "Customer"),
                          trunk: t("pages.live.scope.trunk", "Trunk"),
                          reseller: t("pages.live.scope.reseller", "Reseller"),
                          org: t("pages.live.scope.tenant", "Tenant"),
                        }[c.scope] || c.scope;
                      const cur_total = c.total ?? c.value ?? 0;
                      // Pressure %, used to mark rows visually as "close
                      // to cap" without waiting for the rejection.
                      const pressures = [];
                      if (cap_total > 0) pressures.push(cur_total / cap_total);
                      if (cap_in > 0) pressures.push((c.in || 0) / cap_in);
                      if (cap_out > 0) pressures.push((c.out || 0) / cap_out);
                      const peak = pressures.length ? Math.max(...pressures) : 0;
                      const warn = peak >= 0.8 && peak < 1.0;
                      const hot = peak >= 1.0;
                      return (
                        <div
                          key={`${c.scope}:${c.scope_id}`}
                          className="text-xs"
                          data-testid={`live-channel-row-${c.scope}-${c.scope_id}`}
                          data-pressure={hot ? "hot" : warn ? "warn" : "ok"}
                        >
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="font-medium" title={c.scope_id}>
                              <span className="text-[10px] uppercase tracking-wide text-muted-foreground mr-1.5">
                                {scopeBadge}
                              </span>
                              {label}
                              {hot && (
                                <span className="ml-2 inline-block rounded-full bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] px-1.5 py-0 text-[10px] uppercase tracking-wide">
                                  {t("pages.live.atCap", "at cap")}
                                </span>
                              )}
                              {warn && (
                                <span className="ml-2 inline-block rounded-full bg-[hsl(var(--warn))]/15 text-[hsl(var(--warn))] px-1.5 py-0 text-[10px] uppercase tracking-wide">
                                  {t("pages.live.nearCap", "near cap")}
                                </span>
                              )}
                            </span>
                            <span className="font-mono text-[11px] text-muted-foreground">
                              {cur_total}
                              {(c.in > 0 || c.out > 0) && (
                                <>
                                  {" "}
                                  <span className="text-[hsl(var(--accent))]">↓{c.in || 0}</span>{" "}
                                  <span className="text-[hsl(var(--info))]">↑{c.out || 0}</span>
                                </>
                              )}
                            </span>
                          </div>
                          <ChannelMeter
                            policy={policy}
                            used={cur_total}
                            total={cap_total}
                            used_in={c.in || 0}
                            used_out={c.out || 0}
                            max_in={cap_in}
                            max_out={cap_out}
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-2">{t("pages.live.recentSecurity", "Recent security")}</div>
              {snap.recent_security.length === 0 ? (
                <div className="text-xs text-muted-foreground">{t("pages.live.quiet", "Quiet.")}</div>
              ) : (
                <div className="space-y-1.5">
                  {snap.recent_security.slice(0, 12).map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <StatusPill kind="security">{e.type}</StatusPill>
                      <span className="font-mono">{e.ip}</span>
                      <span className="ml-auto text-muted-foreground">
                        {new Date(e.ts).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Phase 6am (Hotfix Z.6) — Failed REGISTER attempts panel.
              Single-click softphone-troubleshooter. Every REGISTER the
              engine rejected (wrong domain, unknown user, bad password,
              tenant inactive, ACL block) lands here with the exact
              human-readable reason the engine logged. Drastically cuts
              the "Zoiper just says Failed, why?" loop. */}
          <Card data-testid="register-fails-card">
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-2 flex items-center gap-2">
                <UserX className="h-4 w-4 text-[hsl(var(--danger))]" />
                {t("pages.live.registerFails.title", "Failed REGISTER attempts")}
                {registerFails.length > 0 && (
                  <StatusPill kind="danger" size="xs">{registerFails.length}</StatusPill>
                )}
              </div>
              <SectionHelp
                i18nKey="help.live.registerFails"
                text={t(
                  "pages.live.registerFails.help",
                  "Each row is a REGISTER request the engine refused. The 'Why' column tells you exactly which check failed: unknown domain, no such user, wrong password, tenant inactive, or an ACL block. Match the row to your softphone config to fix the registration."
                )}
                className="mt-0 mb-3"
              />
              {registerFails.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  {t(
                    "pages.live.registerFails.quiet",
                    "No failed REGISTERs in the last 200 attempts. If a softphone reports 'Failed' but nothing appears here, the request isn't reaching the engine — check DNS for your SIP domain and that port 5060/UDP is open on the VPS firewall."
                  )}
                </div>
              ) : (
                <div className="max-h-72 overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky-table-head">
                      <TableRow>
                        <TableHead>{t("pages.live.registerFails.cols.when", "When")}</TableHead>
                        <TableHead>{t("pages.live.registerFails.cols.from", "From IP")}</TableHead>
                        <TableHead>{t("pages.live.registerFails.cols.user", "User")}</TableHead>
                        <TableHead>{t("pages.live.registerFails.cols.domain", "Domain")}</TableHead>
                        <TableHead>{t("pages.live.registerFails.cols.status", "Code")}</TableHead>
                        <TableHead>{t("pages.live.registerFails.cols.reason", "Why")}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {registerFails.slice(0, 20).map((tr, i) => {
                        const code = Number(tr.status || 0);
                        const kind =
                          code === 401 ? "warn" :
                          code === 403 ? "danger" :
                          code === 404 ? "warn" :
                          code >= 500 ? "danger" : "info";
                        return (
                          <TableRow
                            key={`${tr.ts}-${i}`}
                            data-testid={`register-fail-row-${i}`}
                            className="text-xs"
                          >
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                              {tr.ts ? new Date(tr.ts).toLocaleTimeString() : "—"}
                            </TableCell>
                            <TableCell className="font-mono">{tr.peer_ip || "—"}</TableCell>
                            <TableCell className="font-mono">{tr.attempted_user || "—"}</TableCell>
                            <TableCell className="font-mono">{tr.attempted_domain || "—"}</TableCell>
                            <TableCell>
                              <StatusPill kind={kind} size="xs">{code || "?"}</StatusPill>
                            </TableCell>
                            <TableCell className="max-w-md truncate" title={tr.reason || ""}>
                              {tr.reason || "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Engine Health KPIs — fed by `/api/live/engine-perf`. */}
          <Card data-testid="engine-health-card">
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4 text-accent" />
                {t("pages.live.engineHealth", "Engine health")}
              </div>
              {!perf ? (
                <div className="text-xs text-muted-foreground">
                  {t("pages.live.engineHealthLoading", "Gathering telemetry…")}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <HealthRow
                    label={t("pages.live.perf.activeCalls", "Active calls")}
                    value={perf.active_calls ?? 0}
                  />
                  <HealthRow
                    label={t("pages.live.perf.activeTx", "In-flight tx")}
                    value={perf.active_transactions ?? 0}
                  />
                  <HealthRow
                    label={t("pages.live.perf.retransmits", "Retransmits")}
                    value={perf.retransmits_total ?? 0}
                  />
                  <HealthRow
                    label={t("pages.live.perf.keepalive", "Keepalive OK")}
                    value={`${Math.round((perf.keepalive_success_ratio || 0) * 100)}%`}
                    hint={`${perf.keepalive_acked_total ?? 0}/${perf.keepalive_sent_total ?? 0}`}
                  />
                  <HealthRow
                    label={t("pages.live.perf.cache", "Cache")}
                    value={perf.cache_entries ?? 0}
                    hint={`ep ${perf.cache_epoch ?? -1}`}
                  />
                  <HealthRow
                    label={t("pages.live.perf.lockedIps", "Locked IPs")}
                    value={perf.locked_ips ?? 0}
                  />
                  <HealthRow
                    label={t("pages.live.perf.uptime", "Uptime")}
                    value={fmtUptime(perf.uptime_seconds)}
                  />
                  <HealthRow
                    label={t("pages.live.perf.host", "Host")}
                    value={perf.host || "—"}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Registrations with reachability pill — fed by
              `/api/live/registrations` which now carries `status` derived
              from OPTIONS keepalive replies. */}
          <Card data-testid="registrations-card">
            <CardContent className="p-4">
              <div className="text-sm font-medium mb-2 flex items-center gap-2">
                <Users2 className="h-4 w-4 text-accent" />
                {t("pages.live.registrations", "Registrations")}
                <span className="text-muted-foreground font-mono">
                  ({regs.length})
                </span>
              </div>
              {regs.length === 0 ? (
                <div className="text-xs text-muted-foreground">
                  {t("pages.live.noRegistrations", "No registered endpoints.")}
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {regs.map((r) => (
                    <div
                      key={r.aor}
                      className="flex items-center gap-2 text-xs py-1 border-b border-border/50 last:border-b-0"
                      data-testid={`reg-row-${r.aor}`}
                    >
                      {/* Phase 6n — Square indicator instead of pill.
                          Same visual grammar as Live call state column
                          and Trunks reachability cell. */}
                      <StatusSquare
                        kind={REG_STATUS_KIND[r.status] || "info"}
                        title={(r.status || "up").toUpperCase()}
                      />
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-8 shrink-0">
                        {(r.status || "up").toUpperCase()}
                      </span>
                      <span className="font-mono truncate flex-1" title={r.aor}>
                        {r.aor}
                      </span>
                      <span className="text-muted-foreground font-mono text-[10px]">
                        {r.source_ip}:{r.source_port}
                      </span>
                      {r.last_rtt_ms != null && (
                        <span className="text-muted-foreground font-mono text-[10px]">
                          {r.last_rtt_ms}ms
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function HealthRow({ label, value, hint }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">
        {value}
        {hint ? (
          <span className="text-muted-foreground text-[10px] ml-1">({hint})</span>
        ) : null}
      </span>
    </div>
  );
}

/**
 * TraceTimelinePane — Phase 6n-f.
 *
 * 3CX/Vodia-flavored real-time SIP trace pane. Renders the chronological
 * timeline of every SIP event captured for the selected active call:
 *
 *   ↓ INVITE      sip:alturas@…           100 Trying
 *   ↑ 100 Trying                          (provisional)
 *   ↑ 401 Unauthorized                    (auth challenge)
 *   ↓ INVITE      auth retry              ──→ Voipms
 *   ↑ 200 OK                              answered
 *   …
 *
 * Each row gets a colored StatusSquare via `traceStatusKind`, an arrow
 * showing direction (in / out / auth-retry), a method/status label,
 * and the engine's stored `summary`. Clicking the X closes the pane.
 */
function TraceTimelinePane({ t, callId, data, loading, onClose }) {
  const items = data?.traces || [];
  const live = data?.live || null;
  return (
    <Card data-testid="live-trace-pane" className="border-[hsl(var(--accent)/0.4)]">
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card sticky top-0 z-10">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-[hsl(var(--accent))]" />
              {t("pages.live.tracePane.title", "Call trace")}
              <span className="font-mono text-[11px] text-muted-foreground truncate">
                {callId}
              </span>
            </div>
            {live && (
              <div className="mt-1 flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                <span className="font-mono">{live.from}</span>
                <span>→</span>
                <span className="font-mono">{live.to}</span>
                {live.tenant_name && (
                  <span className="px-1.5 py-0.5 rounded bg-muted">
                    {live.tenant_name}
                  </span>
                )}
                {live.trunk_name && (
                  <span className="px-1.5 py-0.5 rounded bg-muted">
                    {live.trunk_name}
                  </span>
                )}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="trace-pane-close"
            aria-label={t("common.close", "Close")}
            className="shrink-0 h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        {loading && items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            {t("pages.live.tracePane.loading", "Loading trace…")}
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            {t("pages.live.tracePane.empty", "No SIP events captured for this call yet.")}
          </div>
        ) : (
          <div className="max-h-[520px] overflow-y-auto">
            <ul className="divide-y divide-border">
              {items.map((tr) => {
                const kind = traceStatusKind(tr);
                const dirIcon =
                  tr.direction === "in" ? (
                    <ArrowDown className="h-3 w-3 text-[hsl(var(--info))]" />
                  ) : tr.direction === "out" ? (
                    <ArrowUp className="h-3 w-3 text-[hsl(var(--accent))]" />
                  ) : (
                    <AlertTriangle className="h-3 w-3 text-[hsl(var(--warn))]" />
                  );
                const ts = tr.ts ? new Date(tr.ts) : null;
                const tlabel = ts && !Number.isNaN(ts.getTime())
                  ? `${String(ts.getHours()).padStart(2, "0")}:${String(ts.getMinutes()).padStart(2, "0")}:${String(ts.getSeconds()).padStart(2, "0")}.${String(ts.getMilliseconds()).padStart(3, "0")}`
                  : "—";
                return (
                  <li
                    key={tr.id || `${tr.ts}-${tr.method}`}
                    className="px-4 py-2 flex items-start gap-2.5 text-xs hover:bg-muted/40"
                    data-testid={`trace-row-${tr.id || tr.ts}`}
                  >
                    <span className="font-mono text-[10px] text-muted-foreground shrink-0 w-[88px] pt-0.5">
                      {tlabel}
                    </span>
                    <span className="shrink-0 pt-0.5">{dirIcon}</span>
                    <StatusSquare kind={kind} className="mt-1 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-[12px] text-foreground">
                        <span className="font-semibold">{tr.method || "—"}</span>
                        {tr.status && (
                          <span className="ml-2 text-muted-foreground">
                            {tr.status}
                          </span>
                        )}
                        {tr.peer_ip && (
                          <span className="ml-2 text-muted-foreground text-[10px]">
                            {tr.peer_ip}
                          </span>
                        )}
                      </div>
                      {tr.summary && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 break-words">
                          {tr.summary}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
